/**
 * SAMA Home Page - Premium Wellness Brand Experience
 * Updated with CMS integration for dynamic content
 */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  ArrowRight, MapPin, ChevronRight, ChevronLeft, Sparkles, Calendar, Clock, 
  Star, Instagram, ExternalLink, Users, Award, Heart, 
  CheckCircle, Quote, Menu, X, Mail, Phone, MessageCircle
} from 'lucide-react';
import { CarouselSimple } from '../components/ui/carousel-simple';

// WhatsApp Icon Component
const WhatsAppIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// Logo URLs - Default fallbacks
const SAMA_LOGO_PRIMARY = "https://ehvwjbglvapazrvyzevw.supabase.co/storage/v1/object/public/cms-assets/logos/SAMA_Logo_Primary.png";
const SAMA_LOGO_MARK = "https://ehvwjbglvapazrvyzevw.supabase.co/storage/v1/object/public/cms-assets/logos/SAMA_Logo_Mark.png";

// API URL - Always use relative paths
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Default Instructor data (fallback if CMS is empty)
const defaultInstructors = [
  { id: 1, name: 'Founder', role: 'Master Trainer', image: 'https://ehvwjbglvapazrvyzevw.supabase.co/storage/v1/object/public/cms-assets/instructors/instructor_founder.jpeg' },
  { id: 2, name: 'Instructor', role: 'Pilates Expert', image: 'https://ehvwjbglvapazrvyzevw.supabase.co/storage/v1/object/public/cms-assets/instructors/instructor_5.jpeg' },
  { id: 3, name: 'Instructor', role: 'Yoga Specialist', image: 'https://ehvwjbglvapazrvyzevw.supabase.co/storage/v1/object/public/cms-assets/instructors/instructor_4.jpeg' },
  { id: 4, name: 'Expert', role: 'Aerial Coach', image: 'https://images.pexels.com/photos/8436641/pexels-photo-8436641.jpeg' },
  { id: 5, name: 'Trainer', role: 'Breathwork Guide', image: 'https://images.pexels.com/photos/7363322/pexels-photo-7363322.jpeg' },
  { id: 6, name: 'Coach', role: 'Meditation Lead', image: 'https://images.pexels.com/photos/6643163/pexels-photo-6643163.jpeg' }
];

// Default Programs data (fallback if CMS is empty)
const defaultPrograms = [
  { id: 1, name: 'Mat Pilates', image: 'https://ehvwjbglvapazrvyzevw.supabase.co/storage/v1/object/public/cms-assets/programs/mat_pilates.avif' },
  { id: 2, name: 'Aerial Yoga', image: 'https://ehvwjbglvapazrvyzevw.supabase.co/storage/v1/object/public/cms-assets/programs/aerial_yoga.avif' },
  { id: 3, name: 'Reformer Pilates', image: 'https://images.pexels.com/photos/18499504/pexels-photo-18499504.png' },
  { id: 4, name: 'Yoga Flow', image: 'https://images.unsplash.com/photo-1763699751431-dff04d232842?crop=entropy&cs=srgb&fm=jpg&q=85' },
  { id: 5, name: 'Breathwork', image: 'https://images.unsplash.com/photo-1588040217484-7d484d97dbe4?crop=entropy&cs=srgb&fm=jpg&q=85' },
  { id: 6, name: 'Meditation', image: 'https://images.pexels.com/photos/7363322/pexels-photo-7363322.jpeg' }
];

// Default Studios data (fallback if CMS is empty)
const defaultStudios = [
  { id: 1, name: 'Whitefield Studio', city: 'Bangalore', image: 'https://images.unsplash.com/photo-1676496962536-d8ef110ff6f0?crop=entropy&cs=srgb&fm=jpg&q=85' },
  { id: 2, name: 'Whitefield Studio 2', city: 'Bangalore', image: 'https://images.unsplash.com/photo-1761971975973-cbb3e59263de?crop=entropy&cs=srgb&fm=jpg&q=85' },
  { id: 3, name: 'Financial District', city: 'Hyderabad', image: 'https://images.unsplash.com/photo-1636990628724-cb59f83326d7?crop=entropy&cs=srgb&fm=jpg&q=85' }
];

// Default Testimonials data (fallback if CMS is empty)
const defaultTestimonials = [
  { id: 1, name: 'Priya Sharma', role: 'Member since 2023', image: 'https://images.pexels.com/photos/3768163/pexels-photo-3768163.jpeg?auto=compress&cs=tinysrgb&w=150', quote: 'SAMA transformed my relationship with fitness. The instructors are incredibly attentive, and every class feels personalized.', rating: 5 },
  { id: 2, name: 'Rahul Menon', role: 'Member since 2022', image: 'https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=150', quote: 'The aerial yoga classes are unlike anything else in the city. World-class facility with instructors who truly care.', rating: 5 },
  { id: 3, name: 'Ananya Reddy', role: 'Member since 2024', image: 'https://images.pexels.com/photos/3756679/pexels-photo-3756679.jpeg?auto=compress&cs=tinysrgb&w=150', quote: 'Found my calm at SAMA. The breathwork sessions have helped me manage stress better than anything else.', rating: 5 }
];

// Default Stats data (fallback if CMS is empty)
const defaultStats = [
  { value: '5000+', label: 'Classes Conducted', icon: Calendar },
  { value: '1500+', label: 'Active Members', icon: Users },
  { value: '15+', label: 'Expert Instructors', icon: Award },
  { value: '3', label: 'Studio Locations', icon: MapPin }
];

// Default How it works steps (fallback if CMS is empty)
const defaultHowItWorks = [
  { step: 1, title: 'Sign Up', description: 'Create your account and choose a membership plan that fits your lifestyle.', icon: Users },
  { step: 2, title: 'Book Classes', description: 'Browse our schedule and book your favorite classes with just a tap.', icon: Calendar },
  { step: 3, title: 'Transform', description: 'Show up, practice with our expert instructors, and feel the difference.', icon: Sparkles }
];

// Icon mapping for CMS content
const iconMap = {
  Calendar, Clock, Star, Users, Award, Heart, MapPin, Sparkles, CheckCircle
};

// Navigation menu items
const menuItems = [
  { label: 'Home', href: '/', section: null },
  { label: 'Weekly Schedule', href: '/weekly-schedule', section: null },
  { label: 'Book a Class', href: '/schedule', section: null },
  { label: 'Find Your Practice', href: '#find-practice', section: 'find-practice' },
  { label: 'Membership Plans', href: '/packages', section: null },
  { label: 'Our Studios', href: '#studios', section: 'studios' },
  { label: 'Instructors', href: '#instructors', section: 'instructors' },
  { label: 'About SAMA', href: '#philosophy', section: 'philosophy' },
  { label: 'FAQ', href: '/faq', section: null },
  { label: 'Contact', href: '#footer', section: 'footer' },
];

// Animated Counter Hook
const useCountUp = (end, duration = 2000, startOnView = true) => {
  const [count, setCount] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!startOnView || hasStarted) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasStarted) {
          setHasStarted(true);
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [startOnView, hasStarted]);

  useEffect(() => {
    if (!hasStarted) return;

    const numericEnd = parseInt(end.replace(/\D/g, ''));
    const increment = numericEnd / (duration / 16);
    let current = 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= numericEnd) {
        setCount(numericEnd);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, 16);

    return () => clearInterval(timer);
  }, [hasStarted, end, duration]);

  const suffix = end.replace(/[0-9]/g, '');
  return { count: `${count}${suffix}`, ref };
};

// Tiled Logo Mark Background Component - DISABLED
const TiledLogoBackground = ({ opacity = 0.25 }) => null;

// Package Card Component - Premium dark styling
const PackageCard = ({ pkg, onClick }) => {
  const isUnlimited = pkg.validity_days > 365;
  const validityText = isUnlimited ? 'No expiry' : `${pkg.validity_days} days`;
  const isFeatured = pkg.is_featured || false;
  const badge = pkg.badge || (isUnlimited && !isFeatured ? 'BEST VALUE' : null);
  
  return (
    <button
      onClick={onClick}
      className={`group relative w-full h-full p-6 md:p-8 border-2 transition-all duration-300 text-left overflow-hidden
        ${isFeatured 
          ? 'bg-gradient-to-br from-[#2F5D50] to-[#1e3d34] border-[#2F5D50] shadow-2xl shadow-[#2F5D50]/20' 
          : 'bg-white border-stone-200 hover:border-[#2F5D50]/50 hover:shadow-xl'
        }`}
      style={{
        borderRadius: 'var(--theme-card-radius, 24px)',
      }}
    >
      {/* Badge - from package data */}
      {badge && (
        <div className="absolute top-0 right-0">
          <div className={`text-xs font-bold px-4 py-1.5 rounded-bl-xl
            ${isFeatured ? 'bg-yellow-400 text-stone-900' : 'bg-[#2F5D50] text-white'}`}>
            {badge}
          </div>
        </div>
      )}
      
      {/* Package name */}
      <h3 className={`text-xl md:text-2xl font-heading mb-2 pr-20
        ${isFeatured ? 'text-white' : 'text-stone-900'}`}>
        {pkg.name || 'Credit Package'}
      </h3>
      
      {/* Description */}
      {pkg.description && (
        <p className={`text-sm mb-6 line-clamp-2
          ${isFeatured ? 'text-white/70' : 'text-stone-500'}`}>
          {pkg.description}
        </p>
      )}
      
      {/* Price - prominent */}
      <div className="mb-6">
        <div className="flex items-baseline gap-1">
          <span className={`text-4xl md:text-5xl font-bold
            ${isFeatured ? 'text-white' : 'text-stone-900'}`}>
            ₹{(pkg.price_inr || 0).toLocaleString()}
          </span>
        </div>
      </div>
      
      {/* Features */}
      <div className="space-y-3 mb-8">
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center
            ${isFeatured ? 'bg-white/20' : 'bg-[#2F5D50]/10'}`}>
            <Sparkles className={`w-4 h-4 ${isFeatured ? 'text-yellow-400' : 'text-[#2F5D50]'}`} />
          </div>
          <span className={`font-semibold ${isFeatured ? 'text-white' : 'text-stone-900'}`}>
            {pkg.credits_granted} Credits
          </span>
        </div>
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center
            ${isFeatured ? 'bg-white/20' : 'bg-stone-100'}`}>
            <Clock className={`w-4 h-4 ${isFeatured ? 'text-white/70' : 'text-stone-500'}`} />
          </div>
          <span className={isFeatured ? 'text-white/70' : 'text-stone-500'}>
            Valid for {validityText}
          </span>
        </div>
      </div>
      
      {/* CTA Button */}
      <div className={`w-full py-4 rounded-xl font-semibold text-center transition-all duration-300 group-hover:scale-[1.02]
        ${isFeatured 
          ? 'bg-white text-[#2F5D50] shadow-lg' 
          : 'bg-[#2F5D50] text-white group-hover:bg-[#1e3d34]'
        }`}>
        Get Started
        <ArrowRight className="inline-block w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
      </div>
    </button>
  );
};

// Section Header Component - Premium styling
const SectionHeader = ({ title, subtitle, action, onActionClick, className = '' }) => (
  <div className={`flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12 ${className}`}>
    <div>
      <h2 className="text-2xl md:text-3xl font-heading text-foreground mb-3 animate-slide-up tracking-tight">
        {title}
      </h2>
      {subtitle && (
        <p className="text-muted-foreground text-base max-w-lg animate-slide-up delay-100 font-light">
          {subtitle}
        </p>
      )}
    </div>
    {action && (
      <button 
        onClick={onActionClick}
        className="flex items-center gap-2 text-primary font-medium text-sm hover:gap-3 transition-all focus-ring rounded-lg px-3 py-2"
      >
        {action}
        <ChevronRight className="w-4 h-4" />
      </button>
    )}
  </div>
);

// Stat Card Component - Premium styling
const StatCard = ({ stat, index }) => {
  const { count, ref } = useCountUp(stat.value);
  const Icon = stat.icon;
  
  return (
    <div 
      ref={ref}
      className={`text-center p-8 animate-slide-up`}
      style={{ animationDelay: `${index * 0.1}s` }}
    >
      <div className="w-14 h-14 mx-auto mb-5 rounded-2xl bg-primary/10 flex items-center justify-center">
        <Icon className="w-7 h-7 text-primary" />
      </div>
      <div className="text-3xl md:text-4xl font-heading text-foreground mb-2">
        {count}
      </div>
      <div className="text-sm text-muted-foreground font-light">
        {stat.label}
      </div>
    </div>
  );
};

// Testimonial Card Component - Premium styling
const TestimonialCard = ({ testimonial, index }) => (
  <div 
    className="w-full bg-white rounded-3xl p-8 border border-stone-200/60 hover:shadow-xl hover:shadow-stone-200/50 transition-all duration-300 animate-slide-up"
  >
    <div className="flex items-center gap-1 mb-5">
      {[...Array(testimonial.rating)].map((_, i) => (
        <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
      ))}
    </div>
    
    <Quote className="w-8 h-8 text-primary/15 mb-4" />
    
    <p className="text-foreground mb-6 leading-relaxed">
      "{testimonial.quote}"
    </p>
    
    <div className="flex items-center gap-4 pt-5 border-t border-stone-100">
      <img 
        src={testimonial.image}
        alt={testimonial.name}
        className="w-12 h-12 rounded-full object-cover"
      />
      <div>
        <p className="font-medium text-foreground">{testimonial.name}</p>
        <p className="text-sm text-muted-foreground">{testimonial.role}</p>
      </div>
    </div>
  </div>
);

// Hamburger Menu Component
const HamburgerMenu = ({ isOpen, onClose, navigate }) => {
  const handleNavigation = (item) => {
    if (item.section) {
      // Scroll to section
      const element = document.getElementById(item.section);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else if (item.href.startsWith('/')) {
      navigate(item.href);
    }
    onClose();
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className={`fixed inset-0 bg-black/40 backdrop-blur-sm z-50 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />
      
      {/* Slide Panel */}
      <div 
        className={`fixed top-0 right-0 h-full w-80 max-w-[85vw] bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-stone-100">
          <img 
            src={SAMA_LOGO_PRIMARY}
            alt="SAMA"
            className="h-16 w-auto object-contain"
          />
          <button
            onClick={onClose}
            className="p-2 hover:bg-stone-100 rounded-full transition-colors"
            aria-label="Close menu"
          >
            <X className="w-6 h-6 text-foreground" />
          </button>
        </div>
        
        {/* Menu Items */}
        <nav className="p-6">
          <ul className="space-y-2">
            {menuItems.map((item, index) => (
              <li key={index}>
                <button
                  onClick={() => handleNavigation(item)}
                  className="w-full text-left px-4 py-3 text-foreground hover:text-primary hover:bg-stone-50 rounded-xl transition-all font-medium"
                >
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
          
          {/* Divider */}
          <div className="my-6 border-t border-stone-100" />
          
          {/* CTA Buttons */}
          <div className="space-y-3">
            <button
              onClick={() => { navigate('/login'); onClose(); }}
              className="w-full px-6 py-3 bg-primary text-white rounded-full font-medium hover:bg-primary/90 transition-all"
            >
              Sign In
            </button>
            <button
              onClick={() => { navigate('/find-my-class'); onClose(); }}
              className="w-full px-6 py-3 bg-stone-800 text-white rounded-full font-medium hover:bg-stone-900 transition-all flex items-center justify-center gap-2"
            >
              <Sparkles className="w-4 h-4" />
              Find Your First Class
            </button>
          </div>
        </nav>
        
        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-6 bg-stone-50 border-t border-stone-100">
          <p className="text-xs text-muted-foreground text-center">
            The Calm Mind Studio
          </p>
        </div>
      </div>
    </>
  );
};

export default function Home() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();
  const [packages, setPackages] = useState([]);
  const [packagesLoading, setPackagesLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [cmsContent, setCmsContent] = useState([]);
  const [cmsLoading, setCmsLoading] = useState(true);
  const [isPreviewMode, setIsPreviewMode] = useState(false);

  // Check for preview mode
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    setIsPreviewMode(urlParams.get('preview') === 'theme');
  }, []);

  // Redirect authenticated users
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      navigate('/schedule');
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Fetch CMS content
  useEffect(() => {
    async function fetchCmsContent() {
      try {
        const response = await fetch(`${API_URL}/api/admin/cms/content/public?sections=hero,value_props,cta_buttons,stats,instructors,programs,studios,testimonials,how_it_works,section_headings,about,footer,contact,logos,packages_section,page_layout,theme`);
        if (response.ok) {
          const data = await response.json();
          setCmsContent(data);
        }
      } catch (error) {
        console.error('Failed to fetch CMS content:', error);
      } finally {
        setCmsLoading(false);
      }
    }
    fetchCmsContent();
  }, []);

  // Helper to get CMS content by section and key
  const getCmsContent = (section, key = null) => {
    const items = cmsContent.filter(item => item.section === section && item.is_active);
    if (key) {
      return items.find(item => item.metadata?.key === key);
    }
    return items.sort((a, b) => (a.order_index || 0) - (b.order_index || 0));
  };

  // Get CMS content for all sections with fallbacks
  const heroHeadline = getCmsContent('hero', 'headline');
  const heroTagline = getCmsContent('hero', 'tagline');
  const heroLocations = getCmsContent('hero', 'locations');
  const heroImage = getCmsContent('hero', 'hero_image');
  const heroMembersStat = getCmsContent('hero', 'members_stat'); // For the floating badge
  const valueProps = getCmsContent('value_props');
  const ctaButtons = getCmsContent('cta_buttons');
  const cmsStats = getCmsContent('stats');
  const cmsInstructors = getCmsContent('instructors');
  const cmsPrograms = getCmsContent('programs');
  const cmsStudios = getCmsContent('studios');
  const cmsTestimonials = getCmsContent('testimonials');
  const cmsHowItWorks = getCmsContent('how_it_works');
  const sectionHeadings = getCmsContent('section_headings');
  const aboutContent = getCmsContent('about');
  const footerContent = getCmsContent('footer');
  const contactContent = getCmsContent('contact');
  const cmsLogos = getCmsContent('logos');
  const cmsTrustBadges = getCmsContent('packages_section');

  // Get page layout (section order) - use the FIRST matching entry
  const sectionOrder = useMemo(() => {
    // Find ALL layout entries and use the first one
    const layoutItems = cmsContent.filter(item => 
      item.section === 'page_layout' && item.metadata?.key === 'section_order'
    );
    const layoutItem = layoutItems.length > 0 ? layoutItems[0] : null;
    
    if (layoutItem?.metadata?.sections) {
      return layoutItem.metadata.sections;
    }
    // Default order
    return [
      { id: 'hero', visible: true },
      { id: 'value_props', visible: true },
      { id: 'programs', visible: true },
      { id: 'how_it_works', visible: true },
      { id: 'stats', visible: true },
      { id: 'instructors', visible: true },
      { id: 'packages', visible: true },
      { id: 'studios', visible: true },
      { id: 'testimonials', visible: true },
      { id: 'philosophy', visible: true },
      { id: 'contact', visible: true },
    ];
  }, [cmsContent]);

  // Helper to check if section is visible
  const isSectionVisible = (sectionId) => {
    const section = sectionOrder.find(s => s.id === sectionId);
    return section ? section.visible !== false : true;
  };

  // Section visibility wrapper - renders children only if section is visible
  const Section = ({ id, children }) => {
    if (!isSectionVisible(id)) return null;
    return children;
  };

  // Get the ordered list of visible section IDs (excluding hero which is always first)
  const orderedSectionIds = useMemo(() => {
    return sectionOrder
      .filter(s => s.visible !== false && s.id !== 'hero' && s.id !== 'value_props')
      .map(s => s.id);
  }, [sectionOrder]);

  // Helper function to get section order index (for dynamic rendering)
  const getSectionOrder = (sectionId) => {
    const idx = orderedSectionIds.indexOf(sectionId);
    return idx >= 0 ? idx : 999; // Put unknown sections at end
  };

  // OrderedSection wrapper - applies CSS order property based on CMS settings
  const OrderedSection = ({ id, children }) => {
    if (!isSectionVisible(id)) return null;
    const order = getSectionOrder(id);
    return (
      <div style={{ order }} className="w-full">
        {children}
      </div>
    );
  };

  // Get logo from CMS with size settings
  const primaryLogo = useMemo(() => {
    const logo = cmsLogos?.find(l => l.metadata?.key === 'primary_logo');
    return {
      url: logo?.media_url || SAMA_LOGO_PRIMARY,
      headerHeight: logo?.metadata?.header_height || 80, // pixels for mobile
      headerHeightMd: logo?.metadata?.header_height_md || 112, // pixels for desktop
      footerHeight: logo?.metadata?.footer_height || 80,
    };
  }, [cmsLogos]);

  // Transform CMS data to component-friendly format with fallbacks
  const instructors = useMemo(() => {
    if (cmsInstructors && cmsInstructors.length > 0) {
      return cmsInstructors.map((item, idx) => ({
        id: item.id || idx + 1,
        name: item.title || 'Instructor',
        role: item.subtitle || 'Wellness Expert',
        image: item.media_url || defaultInstructors[idx]?.image || '',
        // CMS Link URL → per-instructor profile (Instagram, website,
        // dedicated bio page, etc). Same pattern as studios + programs.
        link_url: item.link_url || null,
      }));
    }
    return defaultInstructors;
  }, [cmsInstructors]);

  const programs = useMemo(() => {
    if (cmsPrograms && cmsPrograms.length > 0) {
      return cmsPrograms.map((item, idx) => ({
        id: item.id || idx + 1,
        name: item.title || 'Program',
        image: item.media_url || defaultPrograms[idx]?.image || '',
        // Admin can point each tile at a course page via the CMS `link_url`
        // field (e.g. `/class/reformer-pilates`). Falls back to /schedule
        // when no link is set so the click is never a dead-end.
        link_url: item.link_url || null,
      }));
    }
    return defaultPrograms;
  }, [cmsPrograms]);

  const studios = useMemo(() => {
    if (cmsStudios && cmsStudios.length > 0) {
      return cmsStudios.map((item, idx) => ({
        id: item.id || idx + 1,
        name: item.title || 'Studio',
        city: item.subtitle || item.metadata?.city || 'Location',
        image: item.media_url || defaultStudios[idx]?.image || '',
        // The CMS "Link URL" field holds the Google Maps URL. We surface it
        // on each tile so admins can manage maps location without code.
        link_url: item.link_url || null,
      }));
    }
    return defaultStudios;
  }, [cmsStudios]);

  const testimonials = useMemo(() => {
    if (cmsTestimonials && cmsTestimonials.length > 0) {
      return cmsTestimonials.map((item, idx) => ({
        id: item.id || idx + 1,
        name: item.title || 'Member',
        role: item.subtitle || 'Member',
        image: item.media_url || defaultTestimonials[idx]?.image || '',
        quote: item.description || '',
        rating: item.metadata?.rating || 5
      }));
    }
    return defaultTestimonials;
  }, [cmsTestimonials]);

  const stats = useMemo(() => {
    if (cmsStats && cmsStats.length > 0) {
      return cmsStats.map((item, idx) => ({
        value: item.title || '0',
        label: item.subtitle || 'Metric',
        icon: iconMap[item.metadata?.icon] || defaultStats[idx]?.icon || Calendar
      }));
    }
    return defaultStats;
  }, [cmsStats]);

  const howItWorks = useMemo(() => {
    if (cmsHowItWorks && cmsHowItWorks.length > 0) {
      return cmsHowItWorks.map((item, idx) => ({
        step: item.metadata?.step || idx + 1,
        title: item.title || 'Step',
        description: item.description || '',
        icon: iconMap[item.metadata?.icon] || defaultHowItWorks[idx]?.icon || Calendar
      }));
    }
    return defaultHowItWorks;
  }, [cmsHowItWorks]);

  // Get section headings from CMS
  const getSectionHeading = (key) => {
    if (sectionHeadings && sectionHeadings.length > 0) {
      return sectionHeadings.find(h => h.metadata?.key === key);
    }
    return null;
  };

  const programsHeading = getSectionHeading('programs_heading');
  const instructorsHeading = getSectionHeading('instructors_heading');
  const testimonialsHeading = getSectionHeading('testimonials_heading');
  const howItWorksHeading = getSectionHeading('how_it_works_heading');
  const packagesHeading = getSectionHeading('packages_heading');
  const studiosHeading = getSectionHeading('studios_heading');
  const statsHeading = getSectionHeading('stats_heading');

  // Get WhatsApp number from CMS "default" landing page (no page_key = default)
  const whatsappData = useMemo(() => {
    const whatsappItem = cmsContent.find(item => 
      item.section === 'landing_page' && item.metadata?.key === 'whatsapp' && !item.metadata?.page_key
    ) || cmsContent.find(item => 
      item.section === 'landing_page' && item.metadata?.key === 'whatsapp'
    );
    const number = (whatsappItem?.title || '917075969111').trim().replace(/\D/g, '');
    const message = encodeURIComponent(whatsappItem?.subtitle || 'Hi, I want to know more about SAMA packages');
    return {
      number,
      displayNumber: number.replace(/^91/, '+91 '),
      url: `https://wa.me/${number}?text=${message}`
    };
  }, [cmsContent]);

  // Fetch packages
  useEffect(() => {
    async function fetchPackages() {
      try {
        const response = await fetch(`${API_URL}/api/credits/packages`);
        if (response.ok) {
          const data = await response.json();
          setPackages(data);
        }
      } catch (error) {
        console.error('Failed to fetch packages:', error);
      } finally {
        setPackagesLoading(false);
      }
    }
    fetchPackages();
  }, []);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [menuOpen]);

  const handlePackageClick = (pkg) => {
    const slug = pkg.name?.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') || pkg.id;
    navigate(`/plans/${slug}`);
  };

  return (
    <div className="min-h-screen bg-[#F7F6F3] overflow-x-hidden" data-testid="home-page">
      {/* Preview Mode Banner */}
      {isPreviewMode && (
        <div className="fixed top-0 left-0 right-0 z-[100] bg-gradient-to-r from-amber-500 to-orange-500 text-white py-2 px-4 text-center shadow-lg">
          <div className="flex items-center justify-center gap-2 text-sm font-medium">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            <span>Theme Preview Mode</span>
            <span className="opacity-75">•</span>
            <span className="opacity-90">This is a draft preview. Changes are not published yet.</span>
            <button 
              onClick={() => window.close()}
              className="ml-4 px-3 py-1 bg-white/20 hover:bg-white/30 rounded-full text-xs font-medium transition-colors"
            >
              Close Preview
            </button>
          </div>
        </div>
      )}
      
      {/* ================================
          HEADER - Crystal Glass Effect
          ================================ */}
      <header className={`fixed left-0 right-0 z-40 bg-white/70 backdrop-blur-xl border-b border-white/20 shadow-sm ${isPreviewMode ? 'top-10' : 'top-0'}`}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-3 flex justify-between items-center">
          {/* Primary logo - size controlled via CMS */}
          <img 
            src={primaryLogo.url}
            alt="SAMA - The Calm Mind Studio"
            className="w-auto object-contain"
            style={{ 
              height: `${primaryLogo.headerHeight}px`,
              imageRendering: '-webkit-optimize-contrast'
            }}
          />
          <style>{`
            @media (min-width: 768px) {
              header img[alt="SAMA - The Calm Mind Studio"] {
                height: ${primaryLogo.headerHeightMd}px !important;
              }
            }
          `}</style>
          
          {/* Desktop Nav */}
          <div className="flex items-center gap-3 md:gap-4">
            <button
              onClick={() => navigate('/schedule')}
              className="hidden md:inline-flex px-5 py-2.5 text-[#2C2C2C] hover:text-[#2F5D50] font-medium text-sm transition-colors duration-300"
            >
              Find Classes
            </button>
            <button
              onClick={() => navigate('/login')}
              className="px-6 py-2.5 bg-[#2F5D50] text-white rounded-full text-sm font-medium transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg"
              data-testid="header-signin-btn"
            >
              Sign In
            </button>
            <button
              onClick={() => setMenuOpen(true)}
              className="p-2.5 hover:bg-[#2F5D50]/10 rounded-full transition-colors duration-300"
              aria-label="Open menu"
              data-testid="hamburger-btn"
            >
              <Menu className="w-6 h-6 text-[#2C2C2C]" />
            </button>
          </div>
        </div>
      </header>

      {/* Hamburger Menu */}
      <HamburgerMenu 
        isOpen={menuOpen} 
        onClose={() => setMenuOpen(false)} 
        navigate={navigate}
      />

      {/* ================================
          1. HERO SECTION - Split Asymmetric Layout (CMS-Powered)
          ================================ */}
      <section className={`relative pb-20 md:pb-32 overflow-hidden bg-[#F7F6F3] ${isPreviewMode ? 'pt-36 md:pt-44' : 'pt-28 md:pt-36'}`}>
        {/* Subtle texture overlay */}
        <div 
          className="absolute inset-0 opacity-[0.03] pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`
          }}
        />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-8 md:gap-12 items-center">
            {/* Left Content - 7 columns */}
            <div className="md:col-span-7 text-left">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading text-[#2C2C2C] leading-[1.1] mb-6 animate-slide-up tracking-tight">
                {heroHeadline?.title || 'Stronger. Calmer.'}
                <br />
                <span className="italic text-[#2F5D50]">{heroHeadline?.subtitle || 'For Life.'}</span>
              </h1>
              
              <p className="text-lg md:text-xl text-[#6B6B6B] font-light mb-4 max-w-xl leading-relaxed animate-slide-up" style={{ animationDelay: '100ms' }}>
                {heroHeadline?.description || "Discover beginner-friendly, no pressure balanced wellness routines designed for life's busiest years."}
              </p>
              
              <p className="text-lg md:text-xl text-[#2F5D50] font-medium mb-6 animate-slide-up" style={{ animationDelay: '150ms' }}>
                {heroTagline?.title || 'Staying consistent is now easy!'}
              </p>
              
              {/* Locations */}
              <div className="flex items-center gap-2 mb-10 animate-slide-up" style={{ animationDelay: '180ms' }}>
                <MapPin className="w-5 h-5 text-[#2F5D50]" />
                <span className="text-[#6B6B6B] font-medium">{heroLocations?.title || 'Bengaluru | Hyderabad'}</span>
              </div>
              
              {/* CTA Buttons - from CMS or defaults */}
              <div className="flex flex-col sm:flex-row items-start gap-4 animate-slide-up" style={{ animationDelay: '200ms' }}>
                {/* Primary CTA - Explore Classes */}
                <button
                  onClick={() => {
                    const url = ctaButtons?.find(b => b.metadata?.key === 'primary_cta')?.link_url || '/schedule';
                    if (url.startsWith('#') || url.startsWith('/#')) {
                      const elementId = url.replace('/#', '').replace('#', '');
                      document.getElementById(elementId)?.scrollIntoView({ behavior: 'smooth' });
                    } else {
                      navigate(url);
                    }
                  }}
                  className="w-full sm:w-auto px-8 py-4 bg-[#2F5D50] text-white rounded-full font-medium flex items-center justify-center gap-3 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_32px_rgba(47,93,80,0.25)]"
                  data-testid="browse-classes-btn"
                >
                  <Calendar className="w-5 h-5" />
                  View Class Schedule
                </button>

                {/* Book A Workshop — deep link to workshops/events listing */}
                <button
                  onClick={() => navigate('/experiences')}
                  className="w-full sm:w-auto px-8 py-4 bg-white text-[#2F5D50] border-2 border-[#2F5D50] rounded-full font-medium flex items-center justify-center gap-3 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_32px_rgba(47,93,80,0.15)] hover:bg-[#F5F1EA]"
                  data-testid="explore-experiences-btn"
                >
                  <Sparkles className="w-5 h-5" />
                  Book A Workshop
                </button>
                
                {/* SAMA Packages — WhatsApp the studio hotline directly.
                    Number: 6362198060 (updated per user request 2026-02-17). */}
                <a
                  href="https://wa.me/916362198060?text=Hi%2C%20I%27d%20like%20to%20know%20about%20SAMA%20Packages"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto px-8 py-4 bg-[#25D366] text-white rounded-full font-medium flex items-center justify-center gap-3 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_32px_rgba(37,211,102,0.3)] hover:bg-[#20BD5A]"
                  data-testid="whatsapp-packages-btn"
                >
                  <WhatsAppIcon className="w-5 h-5" />
                  <span>Talk to us about SAMA Packages</span>
                </a>
              </div>

              {/* Value Props - from CMS or defaults */}
              <div className="mt-12 flex flex-col sm:flex-row gap-6 animate-slide-up" style={{ animationDelay: '300ms' }}>
                {valueProps && valueProps.length > 0 ? (
                  valueProps.map((item, i) => (
                    <div key={item.id || i} className="flex items-center gap-2 text-[#6B6B6B]">
                      <CheckCircle className="w-4 h-4 text-[#2F5D50] flex-shrink-0" />
                      <span className="text-sm">{item.title}</span>
                    </div>
                  ))
                ) : (
                  [
                    { text: 'Expert-certified instructors' },
                    { text: 'Small batches, personal attention' },
                    { text: 'Flexible scheduling' }
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-[#6B6B6B]">
                      <CheckCircle className="w-4 h-4 text-[#2F5D50] flex-shrink-0" />
                      <span className="text-sm">{item.text}</span>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Right Image - 5 columns */}
            <div className="md:col-span-5 relative animate-slide-up" style={{ animationDelay: '150ms' }}>
              <div className="relative aspect-[4/5] rounded-3xl overflow-hidden shadow-[0_8px_32px_rgba(47,93,80,0.15)]">
                {/* Show skeleton while CMS is loading, then show the image */}
                {cmsLoading ? (
                  <div className="w-full h-full bg-stone-200 animate-pulse" />
                ) : (
                  <img 
                    src={heroImage?.media_url || "https://images.unsplash.com/photo-1592967547589-e94f6cd77af9?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"}
                    alt="Woman practicing yoga"
                    className="w-full h-full object-cover"
                  />
                )}
                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#2F5D50]/20 to-transparent" />
              </div>
              
              {/* Floating Badge - CMS editable */}
              <div className="absolute -bottom-4 -left-4 md:-left-8 bg-white rounded-2xl p-4 shadow-xl animate-float">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#2F5D50]/10 rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6 text-[#2F5D50]" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#2C2C2C]">
                      {heroMembersStat?.title || '1500+'}
                    </p>
                    <p className="text-xs text-[#6B6B6B]">
                      {heroMembersStat?.subtitle || 'Active Members'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* New Here CTA - Centered below */}
          <div className="mt-16 text-center animate-slide-up" style={{ animationDelay: '400ms' }}>
            <button
              onClick={() => navigate('/find-my-class')}
              className="inline-flex items-center gap-3 px-8 py-4 bg-[#2C2C2C] text-white rounded-full font-medium transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
              data-testid="find-first-class-btn"
            >
              <Sparkles className="w-5 h-5" />
              New here? Find Your First Class
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* Flex container for orderable sections */}
      <div className="flex flex-col">

      {/* ================================
          2. FIND YOUR PRACTICE (CMS-Powered with Carousel)
          ================================ */}
      <OrderedSection id="programs">
      <section id="find-practice" className="relative py-20 md:py-28 bg-white overflow-hidden">
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <SectionHeader
            title={programsHeading?.title || "Find Your Practice"}
            subtitle={programsHeading?.subtitle || "From calming yoga flows to strengthening Pilates, discover classes that transform body and mind."}
            action="View All Classes"
            onActionClick={() => navigate('/schedule')}
          />
          
          <CarouselSimple 
            className="from-white"
            itemClassName="w-[280px] md:w-[320px]"
          >
            {programs.map((program, index) => (
              <button
                key={program.id}
                onClick={() => {
                  // CMS-configurable destination per tile. Common values:
                  //   - "/class/<slug>"            → course detail page
                  //   - "https://..."              → external URL (opens in same tab)
                  //   - blank / null               → falls back to /schedule
                  const dest = program.link_url && String(program.link_url).trim();
                  if (dest) {
                    if (/^https?:\/\//i.test(dest)) {
                      window.location.href = dest;
                    } else {
                      navigate(dest);
                    }
                  } else {
                    navigate('/schedule');
                  }
                }}
                className="relative aspect-square w-full overflow-hidden rounded-2xl md:rounded-3xl group cursor-pointer animate-slide-up shadow-lg"
                data-testid={`find-practice-tile-${program.id}`}
              >
                <img 
                  src={program.image}
                  alt={program.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
                  <p className="text-white font-medium text-base md:text-xl">{program.name}</p>
                </div>
                <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-all duration-300">
                  <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                    <ArrowRight className="w-4 h-4 text-white" />
                  </div>
                </div>
              </button>
            ))}
          </CarouselSimple>
        </div>
      </section>
      </OrderedSection>

      {/* ================================
          3. MEMBERSHIP PACKAGES - 2x2 Grid Layout
          ================================ */}
      <OrderedSection id="packages">
      <section id="packages-section" className="relative py-20 md:py-28 bg-gradient-to-b from-stone-100 to-stone-50 overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#2F5D50]/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#2F5D50]/5 rounded-full blur-3xl" />
        </div>
        
        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12">
          {/* Section Header - Centered and impactful */}
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block px-4 py-2 bg-[#2F5D50]/10 text-[#2F5D50] text-sm font-semibold rounded-full mb-4">
              PRICING
            </span>
            <h2 className="text-3xl md:text-5xl font-heading text-stone-900 mb-4">
              {packagesHeading?.title || "Choose Your Journey"}
            </h2>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto">
              {packagesHeading?.subtitle || "Flexible plans designed to fit your lifestyle. Start your transformation today."}
            </p>
          </div>
          
          {packagesLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-[380px] rounded-3xl bg-white animate-pulse" />
              ))}
            </div>
          ) : packages.length > 0 ? (
            <>
              {/* 2x2 Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mb-8">
                {packages.slice(0, 4).map((pkg, index) => (
                  <div 
                    key={pkg.id} 
                    className="animate-slide-up" 
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <PackageCard 
                      pkg={pkg} 
                      onClick={() => handlePackageClick(pkg)}
                    />
                  </div>
                ))}
              </div>
              
              {/* View All CTA */}
              {packages.length > 4 && (
                <div className="text-center">
                  <button
                    onClick={() => navigate('/packages')}
                    className="inline-flex items-center gap-2 px-8 py-4 bg-white border-2 border-stone-200 text-stone-700 rounded-full font-semibold hover:border-[#2F5D50] hover:text-[#2F5D50] transition-all duration-300"
                  >
                    View All {packages.length} Plans
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-16 bg-white rounded-3xl border border-stone-200/50">
              <p className="text-muted-foreground">No packages available at the moment.</p>
            </div>
          )}
          
          {/* Trust indicators - CMS editable with fallbacks */}
          {(() => {
            // Get trust badges from CMS or use defaults
            const trustBadges = cmsTrustBadges && cmsTrustBadges.length > 0
              ? cmsTrustBadges.map(item => item.title || item.content)
              : ['Cancel anytime', 'No hidden fees', 'Instant access'];
            
            return (
              <div className="mt-12 flex flex-wrap items-center justify-center gap-8 text-stone-500 text-sm">
                {trustBadges.map((badge, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-[#2F5D50]" />
                    <span>{badge}</span>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      </section>
      </OrderedSection>

      {/* ================================
          4. INSTRUCTORS (CMS-Powered with Carousel)
          ================================ */}
      <OrderedSection id="instructors">
      <section id="instructors" className="relative py-20 md:py-28 bg-white overflow-hidden">
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <SectionHeader
            title={instructorsHeading?.title || "Meet Our Instructors"}
            subtitle={instructorsHeading?.subtitle || "Expert trainers dedicated to guiding your wellness journey with care and expertise."}
          />
          
          <CarouselSimple 
            className="from-white"
            itemClassName="w-[260px] md:w-[300px]"
          >
            {instructors.map((instructor, index) => {
              /*
               * Team SAMA card → per-instructor link.
               * Admin sets the link via CMS → Instructors → Link URL.
               *  - Set      → render as <a> opening in a new tab so
               *               members don't lose the homepage. Could be
               *               an Instagram / Calendly / bio page; we
               *               don't assume same-origin, so always new tab.
               *  - Unset    → fall back to a non-interactive <div> so
               *               legacy default-team rows don't show a
               *               fake cursor-pointer affordance.
               */
              const profileUrl = instructor.link_url && String(instructor.link_url).trim();
              // Same-origin SAMA team profile (relative `/team/<slug>` or
              // absolute `https://app.samastudio.in/team/<slug>`) → open
              // in the same tab so members stay inside the SPA. Anything
              // else (Instagram, Calendly, personal site) → new tab.
              let isInternalTeamLink = false;
              if (profileUrl) {
                try {
                  const u = new URL(profileUrl, window.location.origin);
                  isInternalTeamLink =
                    u.origin === window.location.origin &&
                    u.pathname.startsWith('/team/');
                } catch (_) {
                  isInternalTeamLink = profileUrl.startsWith('/team/');
                }
              }
              const cardClass = "relative aspect-square w-full overflow-hidden rounded-2xl md:rounded-3xl group animate-slide-up shadow-lg";
              const inner = (
                <>
                  <img
                    src={instructor.image}
                    alt={instructor.name}
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
                    <p className="text-white font-medium text-base md:text-lg">{instructor.name}</p>
                    <p className="text-white/70 text-sm">{instructor.role}</p>
                  </div>
                </>
              );
              return profileUrl ? (
                <a
                  key={instructor.id}
                  href={profileUrl}
                  target={isInternalTeamLink ? '_self' : '_blank'}
                  // `noopener noreferrer` is required for external links —
                  // without it, an external link can read `window.opener`
                  // and run a tabnabbing attack on the SAMA homepage.
                  rel={isInternalTeamLink ? undefined : 'noopener noreferrer'}
                  className={`${cardClass} block cursor-pointer`}
                  aria-label={
                    isInternalTeamLink
                      ? `Open ${instructor.name}'s profile`
                      : `Open ${instructor.name}'s profile (opens in a new tab)`
                  }
                  data-testid={`team-tile-${instructor.id}`}
                >
                  {inner}
                </a>
              ) : (
                <div
                  key={instructor.id}
                  className={cardClass}
                  data-testid={`team-tile-${instructor.id}`}
                >
                  {inner}
                </div>
              );
            })}
          </CarouselSimple>
        </div>
      </section>
      </OrderedSection>

      {/* ================================
          5. SAMA PHILOSOPHY (CMS-Powered)
          ================================ */}
      <OrderedSection id="philosophy">
      <section id="philosophy" className="relative py-20 md:py-28 bg-stone-50 overflow-hidden">
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
            <div className="order-2 md:order-1">
              <h2 className="text-2xl md:text-4xl font-heading text-foreground mb-6 animate-slide-left">
                {aboutContent && aboutContent.length > 0 ? aboutContent[0]?.title : "The SAMA Philosophy"}
              </h2>
              <p className="text-muted-foreground text-lg mb-8 leading-relaxed animate-slide-left delay-100">
                {aboutContent && aboutContent.length > 0 ? aboutContent[0]?.description : 
                  "SAMA means balance — and that's what we create. A sanctuary where strength meets serenity, where movement becomes meditation. Our intimate studios and expert instructors guide you toward a more centered, powerful you."}
              </p>
              <button
                onClick={() => navigate('/schedule')}
                className="px-8 py-4 bg-primary text-white rounded-full font-medium flex items-center gap-3 hover:bg-primary/90 transition-all hover:scale-105 active:scale-95 animate-slide-left delay-200 shadow-lg"
              >
                {aboutContent && aboutContent.length > 0 && aboutContent[0]?.link_text ? aboutContent[0].link_text : "Begin with SAMA"}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
            
            <div className="order-1 md:order-2 animate-slide-right">
              <div className="relative aspect-[4/3] rounded-2xl md:rounded-3xl overflow-hidden shadow-xl">
                <img 
                  src={aboutContent && aboutContent.length > 0 && aboutContent[0]?.media_url ? aboutContent[0].media_url : "https://images.unsplash.com/photo-1529693662653-9d480530a697?crop=entropy&cs=srgb&fm=jpg&q=85"}
                  alt="SAMA Wellness Experience"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
      </OrderedSection>

      {/* ================================
          6. HOW IT WORKS (CMS-Powered)
          ================================ */}
      <OrderedSection id="how_it_works">
      <section className="relative py-20 md:py-28 bg-white overflow-hidden">
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <SectionHeader
            title={howItWorksHeading?.title || "How It Works"}
            subtitle={howItWorksHeading?.subtitle || "Your wellness journey starts in three simple steps."}
          />
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {howItWorks.map((step, index) => {
              const StepIcon = step.icon;
              return (
                <div 
                  key={step.step}
                  className={`relative text-center p-8 md:p-10 bg-white rounded-2xl md:rounded-3xl border border-stone-200/60 hover:shadow-xl transition-all duration-300 animate-slide-up`}
                  style={{ animationDelay: `${index * 0.15}s` }}
                >
                  {/* Step Number */}
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center font-semibold text-sm shadow-lg">
                    {step.step}
                  </div>
                  
                  {/* Icon */}
                  <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-stone-100 flex items-center justify-center">
                    <StepIcon className="w-8 h-8 text-primary" />
                  </div>
                  
                  <h3 className="text-xl font-heading text-foreground mb-4">
                    {step.title}
                  </h3>
                  
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {step.description}
                  </p>

                  {/* Connector line */}
                  {index < howItWorks.length - 1 && (
                    <div className="hidden md:block absolute top-1/2 -right-4 w-8 h-px bg-stone-200" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>
      </OrderedSection>

      {/* ================================
          7. OUR STUDIOS (CMS-Powered with Carousel)
          ================================ */}
      <OrderedSection id="studios">
      <section id="studios" className="relative py-20 md:py-28 bg-stone-50 overflow-hidden">
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <SectionHeader
            title={studiosHeading?.title || "Our Studios"}
            subtitle={studiosHeading?.subtitle || "Premium wellness spaces thoughtfully designed for your transformation."}
          />
          
          <CarouselSimple 
            className="from-stone-50"
            itemClassName="w-[320px] md:w-[400px]"
          >
            {studios.map((studio, index) => {
              /*
               * Studio card → Google Maps deep-link.
               * Admin sets the link via CMS → Studios → Link URL.
               * - When set, we render an <a> that opens in a new tab so
               *   members can navigate without losing the homepage tab.
               * - When unset, we keep the original non-interactive <div>
               *   so the legacy default-studio rows don't show a fake
               *   cursor-pointer affordance.
               */
              const mapUrl = studio.link_url && String(studio.link_url).trim();
              const cardClass = "relative aspect-[4/3] w-full overflow-hidden rounded-2xl md:rounded-3xl group shadow-lg hover:shadow-xl transition-all duration-300 animate-slide-up";
              const inner = (
                <>
                  <img
                    src={studio.image}
                    alt={studio.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
                    <p className="text-white font-medium text-lg md:text-xl mb-1">{studio.name}</p>
                    <div className="flex items-center gap-1.5 text-white/80 text-sm">
                      <MapPin className="w-4 h-4" />
                      <span>{studio.city}</span>
                    </div>
                  </div>
                </>
              );
              return mapUrl ? (
                <a
                  key={studio.id}
                  href={mapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  // `noopener noreferrer` is critical — without it the new
                  // Google Maps tab can hijack `window.opener` and run
                  // `window.opener.location = "phishing.site"`.
                  className={`${cardClass} block cursor-pointer`}
                  aria-label={`Open ${studio.name} on Google Maps (opens in a new tab)`}
                  data-testid={`studio-tile-${studio.id}`}
                >
                  {inner}
                </a>
              ) : (
                <div key={studio.id} className={cardClass} data-testid={`studio-tile-${studio.id}`}>
                  {inner}
                </div>
              );
            })}
          </CarouselSimple>
        </div>
      </section>
      </OrderedSection>

      {/* ================================
          8. WHAT OUR MEMBERS SAY (CMS-Powered with Carousel)
          ================================ */}
      <OrderedSection id="testimonials">
      <section className="relative py-20 md:py-28 bg-white overflow-hidden">
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <SectionHeader
            title={testimonialsHeading?.title || "What Our Members Say"}
            subtitle={testimonialsHeading?.subtitle || "Join a community of wellness enthusiasts who've found their balance at SAMA."}
          />
          
          <CarouselSimple 
            className="from-white"
            itemClassName="w-[320px] md:w-[400px]"
          >
            {testimonials.map((testimonial, index) => (
              <TestimonialCard key={testimonial.id} testimonial={testimonial} index={index} />
            ))}
          </CarouselSimple>
        </div>
      </section>
      </OrderedSection>

      {/* ================================
          9. KEY MILESTONES (CMS-Powered)
          ================================ */}
      <OrderedSection id="stats">
      <section className="relative py-20 md:py-28 bg-stone-50 overflow-hidden">
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <SectionHeader
            title={statsHeading?.title || "Key Milestones"}
            subtitle={statsHeading?.subtitle || "Numbers that reflect our commitment to wellness"}
          />
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
            {stats.map((stat, index) => (
              <StatCard key={stat.label} stat={stat} index={index} />
            ))}
          </div>
        </div>
      </section>
      </OrderedSection>

      {/* ================================
          10. BEGIN YOUR JOURNEY (CTA)
          ================================ */}
      <OrderedSection id="contact">
      <section className="py-20 md:py-28 bg-primary relative overflow-hidden">
        {/* Decorative shapes */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-0 w-64 h-64 bg-white/5 rounded-full -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/5 rounded-full translate-x-1/3 translate-y-1/3" />
        </div>
        
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center relative z-10">
          <h2 className="text-2xl md:text-4xl font-heading text-white mb-4 animate-slide-up">
            Begin Your Journey
          </h2>
          <p className="text-white/80 text-lg mb-10 max-w-xl mx-auto animate-slide-up delay-100">
            Your transformation starts with a single class. Explore our schedule and find 
            the practice that speaks to you.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-slide-up delay-200">
            <button
              onClick={() => navigate('/schedule')}
              className="w-full sm:w-auto px-8 py-4 bg-white text-primary rounded-full font-medium hover:bg-white/95 transition-all shadow-lg flex items-center justify-center gap-2 hover:scale-105 active:scale-95"
            >
              <Calendar className="w-5 h-5" />
              View Class Schedule
            </button>
            <a
              href="https://wa.me/916362198060?text=Hi%2C%20I%27d%20like%20to%20know%20about%20SAMA%20Packages"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto px-8 py-4 bg-[#25D366] text-white rounded-full font-medium hover:bg-[#20BD5A] transition-all flex items-center justify-center gap-2 hover:scale-105 active:scale-95"
              data-testid="whatsapp-packages-cta-bottom"
            >
              <WhatsAppIcon className="w-5 h-5" />
              Talk to us about SAMA Packages
            </a>
          </div>
        </div>
      </section>
      </OrderedSection>

      {/* ================================
          11. FAQ PREVIEW SECTION (Links to /faq)
          ================================ */}
      <OrderedSection id="faq">
      <section className="relative py-20 md:py-28 bg-stone-50 overflow-hidden">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl md:text-4xl font-heading text-stone-800 mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-stone-600 text-lg mb-8 max-w-2xl mx-auto">
            Find answers to common questions about our classes, booking, and studio policies.
          </p>
          <button
            onClick={() => navigate('/faq')}
            className="px-8 py-4 bg-[#2F5D50] text-white rounded-full font-medium hover:bg-[#264A40] transition-all hover:scale-105 active:scale-95"
          >
            View All FAQs
          </button>
        </div>
      </section>
      </OrderedSection>

      </div>{/* End of flex container for orderable sections */}

      {/* ================================
          12. FOOTER - Updated with CMS-powered content
          ================================ */}
      <footer id="footer" className="py-16 md:py-20 bg-stone-900 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Main Footer Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8 mb-12">
            {/* Logo & Tagline */}
            <div className="text-center md:text-left">
              <div className="bg-white rounded-2xl p-4 inline-block">
                <img 
                  src={primaryLogo.url}
                  alt="SAMA"
                  className="w-auto object-contain"
                  style={{ height: `${primaryLogo.footerHeight}px` }}
                />
              </div>
              <p className="text-stone-400 mt-4 text-lg font-light">
                {footerContent?.find(f => f.metadata?.key === 'footer_tagline')?.title || "The Calm Mind Studio"}
              </p>
              <p className="text-stone-500 mt-2 text-sm">
                Pilates • Yoga • Aerial • Wellness
              </p>
            </div>
            
            {/* Contact Info - CMS-powered */}
            <div className="text-center">
              <h3 className="text-lg font-heading text-white mb-6">Get In Touch</h3>
              <div className="space-y-4">
                <a 
                  href={`mailto:${contactContent?.find(c => c.metadata?.key === 'email')?.link_url || 'bookings@samastudio.in'}`}
                  className="flex items-center justify-center gap-3 text-white hover:text-primary transition-colors group"
                >
                  <Mail className="w-5 h-5 text-stone-400 group-hover:text-primary" />
                  <span className="text-xl font-medium">
                    {contactContent?.find(c => c.metadata?.key === 'email')?.title || 'bookings@samastudio.in'}
                  </span>
                </a>
                <a 
                  href={`tel:${contactContent?.find(c => c.metadata?.key === 'phone')?.link_url || '+917075969111'}`}
                  className="flex items-center justify-center gap-3 text-white hover:text-primary transition-colors group"
                >
                  <Phone className="w-5 h-5 text-stone-400 group-hover:text-primary" />
                  <span className="text-xl font-medium">
                    {contactContent?.find(c => c.metadata?.key === 'phone')?.title || '+91 98765 43210'}
                  </span>
                </a>
              </div>
            </div>
            
            {/* Instagram & Social */}
            <div className="text-center md:text-right">
              <h3 className="text-lg font-heading text-white mb-6">Follow Us</h3>
              <a 
                href={contactContent?.find(c => c.metadata?.key === 'instagram')?.link_url || "https://www.instagram.com/sama.studioindia/"}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 text-white hover:text-pink-400 transition-colors group"
              >
                <Instagram className="w-6 h-6" />
                <span className="text-xl font-medium">
                  {contactContent?.find(c => c.metadata?.key === 'instagram')?.title || '@sama.studioindia'}
                </span>
              </a>
              <div className="mt-6 space-y-2">
                <p className="text-stone-400 text-sm">Whitefield, Bangalore</p>
                <p className="text-stone-400 text-sm">Financial District, Hyderabad</p>
              </div>
            </div>
          </div>
          
          {/* Divider */}
          <div className="border-t border-stone-800 pt-8">
            {/* Quick Links */}
            <div className="flex flex-wrap items-center justify-center gap-6 mb-6 text-sm">
              <button onClick={() => navigate('/schedule')} className="text-stone-400 hover:text-white transition-colors">
                Classes
              </button>
              <button onClick={() => navigate('/packages')} className="text-stone-400 hover:text-white transition-colors">
                Packages
              </button>
              <button onClick={() => navigate('/contact')} className="text-stone-400 hover:text-white transition-colors">
                Contact
              </button>
              <a href="https://www.samastudio.in" target="_blank" rel="noopener noreferrer" className="text-stone-400 hover:text-white transition-colors">
                Website
              </a>
              <button onClick={() => navigate('/find-my-class')} className="text-stone-400 hover:text-white transition-colors">
                Find My Class
              </button>
              <button onClick={() => navigate('/privacy-policy')} className="text-stone-400 hover:text-white transition-colors" data-testid="footer-privacy-link">
                Privacy Policy
              </button>
            </div>
            
            {/* Copyright - CMS-powered */}
            <p className="text-center text-stone-500 text-sm">
              {footerContent?.find(f => f.metadata?.key === 'copyright')?.title || '© 2025 SAMA Studio. All rights reserved.'}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Export SAMALogo for use in other components
export const SAMALogo = ({ className = '', size = 'default' }) => {
  const sizes = {
    small: 'h-16',
    default: 'h-24',
    large: 'h-32'
  };
  return (
    <img 
      src={SAMA_LOGO_PRIMARY}
      alt="SAMA - The Calm Mind Studio"
      className={`${sizes[size] || sizes.default} w-auto object-contain ${className}`}
      style={{ imageRendering: '-webkit-optimize-contrast' }}
    />
  );
};

// Export logo URLs for other components
export const LOGO_PRIMARY = SAMA_LOGO_PRIMARY;
export const LOGO_MARK = SAMA_LOGO_MARK;

// Export background component - DISABLED (returns null)
export const SAMABackground = ({ opacity = 0.06 }) => null;
